import { TeamInfo } from '../TeamInfo';
import { RaceRules } from '../types';
import { checkCompliance, driverTimes } from '../analytics/driveTime';
import { stintMinutes } from '../analytics/lapTime';
import { LivePlan, describeStop } from '../analytics/liveStrategy';

export type AlertSeverity = 'info' | 'warn' | 'urgent';

export type AlertKind =
  | 'pit-due'
  | 'pit-over'
  | 'pit-entered'
  | 'pit-exited'
  | 'penalty'
  | 'drive-time'
  | 'session'
  | 'plan-stop-due'
  | 'plan-stop-now'
  | 'plan-stop-overdue'
  | 'plan-driver-mismatch'
  | 'plan-infeasible';

export interface Alert {
  /**
   * Identifies the occurrence, not the alert type — `pit-entered:r1:7` is the
   * seventh stop for that kart. Firing is keyed on this, so the same event
   * cannot raise twice however many frames it spans.
   */
  id: string;
  kind: AlertKind;
  severity: AlertSeverity;
  message: string;
  teamId?: string;
  kartNumber?: string;
  at: number;
}

export interface AlertContext {
  teams: Map<string, TeamInfo>;
  /** Only marked karts raise alerts — the whole field would be noise. */
  watchedTeamIds: Set<string>;
  rules: RaceRules;
  /** Penalty and warning count per kart number. */
  penaltyCountByKart: Map<string, number>;
  minutesRemaining: number | null;
  now: number;
  /**
   * The stint plan reconciled against the race, per team. A team with a plan is
   * announced whether or not it is marked as watched: attaching a plan is the
   * statement that somebody cares about it.
   */
  livePlans?: Map<string, LivePlan>;
}

/** Everything already raised, so nothing fires twice. */
export type FiredAlerts = Set<string>;

/** How far past the planned stop before it is called overdue rather than due. */
const OVERDUE_AFTER_MINUTES = 2;

/** Rules whose breach means the plan can no longer be followed to the flag. */
const UNFOLLOWABLE_RULES = new Set(['3e', '10', '14', '15', '16', '17', '18']);

/** Minutes remaining that are worth calling out. */
const SESSION_MILESTONES = [60, 30, 15, 5, 1];

function kartLabel(team: TeamInfo): string {
  return `#${team.kartNumber || '—'}`;
}

function pitCountOf(team: TeamInfo): number {
  const n = parseInt(team.pitCount, 10);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Compare the current frame against what has already been announced.
 *
 * Deliberately stateless apart from the `fired` set: there is no "previous
 * frame" to diff against, because a replay seeking backwards would make such a
 * diff meaningless. Everything here is derived from the frame in hand plus
 * whether we have said it before.
 */
export function detectAlerts(fired: FiredAlerts, ctx: AlertContext): Alert[] {
  const { teams, watchedTeamIds, rules, penaltyCountByKart, minutesRemaining, now } = ctx;
  const found: Alert[] = [];

  const raise = (alert: Omit<Alert, 'at'>): void => {
    if (fired.has(alert.id)) return;
    fired.add(alert.id);
    found.push({ ...alert, at: now });
  };

  teams.forEach((team, teamId) => {
    if (!watchedTeamIds.has(teamId)) return;

    const stops = pitCountOf(team);
    const label = kartLabel(team);

    if (team.inBox) {
      raise({
        id: `pit-entered:${teamId}:${stops}`,
        kind: 'pit-entered',
        severity: 'info',
        message: `${label} is in the pit`,
        teamId,
        kartNumber: team.kartNumber,
      });
    } else {
      // Only counts as leaving once we have seen it arrive, so a kart that was
      // already out when we connected does not announce a stop that never was.
      if (fired.has(`pit-entered:${teamId}:${stops}`)) {
        raise({
          id: `pit-exited:${teamId}:${stops}`,
          kind: 'pit-exited',
          severity: 'info',
          message: `${label} has left the pit`,
          teamId,
          kartNumber: team.kartNumber,
        });
      }

      const age = stintMinutes(team.onTrackTime);
      if (age !== null) {
        const remaining = rules.maxStintMinutes - age;
        if (remaining < 0) {
          raise({
            id: `pit-over:${teamId}:${stops}`,
            kind: 'pit-over',
            severity: 'urgent',
            message: `${label} is ${Math.abs(Math.round(remaining))}′ over the stint limit`,
            teamId,
            kartNumber: team.kartNumber,
          });
        } else if (remaining <= rules.dueSoonMinutes) {
          raise({
            id: `pit-due:${teamId}:${stops}`,
            kind: 'pit-due',
            severity: 'warn',
            message: `${label} is due to pit — ${Math.round(remaining)}′ of stint left`,
            teamId,
            kartNumber: team.kartNumber,
          });
        }
      }
    }

    const penalties = penaltyCountByKart.get(team.kartNumber) ?? 0;
    if (penalties > 0) {
      raise({
        id: `penalty:${team.kartNumber}:${penalties}`,
        kind: 'penalty',
        severity: 'urgent',
        message: `${label} has a penalty or warning`,
        teamId,
        kartNumber: team.kartNumber,
      });
    }

    const compliance = checkCompliance(driverTimes(team), rules, minutesRemaining);
    for (const driver of compliance.drivers) {
      if (driver.status === 'over-max') {
        raise({
          id: `drive-time:${teamId}:${driver.driver}:over`,
          kind: 'drive-time',
          severity: 'urgent',
          message: `${driver.driver} is past their drive-time limit on ${label}`,
          teamId,
          kartNumber: team.kartNumber,
        });
      } else if (driver.status === 'approaching-max') {
        raise({
          id: `drive-time:${teamId}:${driver.driver}:near`,
          kind: 'drive-time',
          severity: 'warn',
          message: `${driver.driver} is near their drive-time limit on ${label}`,
          teamId,
          kartNumber: team.kartNumber,
        });
      } else if (driver.status === 'at-risk') {
        raise({
          id: `drive-time:${teamId}:${driver.driver}:risk`,
          kind: 'drive-time',
          severity: 'urgent',
          message: `${driver.driver} can no longer reach their minimum on ${label}`,
          teamId,
          kartNumber: team.kartNumber,
        });
      }
    }
  });

  ctx.livePlans?.forEach((live, teamId) => {
    const team = teams.get(teamId);
    if (!team) return;
    const label = kartLabel(team);
    const stop = live.nextStop;

    // A kart in the lane has already made this stop; the next one is not due yet.
    if (stop && !live.boxed) {
      const occurrence = `${teamId}:${stop.stintIndex}`;
      const what = describeStop(stop);

      if (stop.inMinutes <= 0) {
        raise({
          id: `plan-stop-now:${occurrence}`,
          kind: 'plan-stop-now',
          severity: 'urgent',
          message: `${label} box now — ${what}`,
          teamId,
          kartNumber: team.kartNumber,
        });
      } else if (stop.inMinutes <= rules.dueSoonMinutes) {
        raise({
          id: `plan-stop-due:${occurrence}`,
          kind: 'plan-stop-due',
          severity: 'warn',
          message: `${label} planned stop in ${Math.ceil(stop.inMinutes)}′ — ${what}`,
          teamId,
          kartNumber: team.kartNumber,
        });
      }

      if (stop.inMinutes < -OVERDUE_AFTER_MINUTES) {
        raise({
          id: `plan-stop-overdue:${occurrence}`,
          kind: 'plan-stop-overdue',
          severity: 'urgent',
          message: `${label} is ${Math.round(-stop.inMinutes)}′ past its planned stop`,
          teamId,
          kartNumber: team.kartNumber,
        });
      }

      if (!stop.currentDriverIsPlanned) {
        const planned = live.plan.roster[live.stints[stop.stintIndex].plannedDriverIndex ?? -1];
        raise({
          id: `plan-driver-mismatch:${occurrence}`,
          kind: 'plan-driver-mismatch',
          severity: 'warn',
          message:
            `${label} has ${stop.outDriver ?? 'a different driver'} in the kart; ` +
            `the plan has ${planned?.name ?? 'someone else'}`,
          teamId,
          kartNumber: team.kartNumber,
        });
      }
    }

    // Once per distinct problem, not once per stint: a plan that was already
    // illegal when the race began should not repeat itself every stop.
    for (const violation of live.violations) {
      if (violation.severity !== 'error' || !UNFOLLOWABLE_RULES.has(violation.rule)) continue;
      if (violation.stintIndex !== null && violation.stintIndex < live.currentIndex) continue;
      raise({
        id: `plan-infeasible:${teamId}:${violation.rule}:${violation.stintIndex ?? 'plan'}`,
        kind: 'plan-infeasible',
        severity: 'urgent',
        message: `${label} plan: ${violation.message}`,
        teamId,
        kartNumber: team.kartNumber,
      });
    }
  });

  if (minutesRemaining !== null) {
    for (const milestone of SESSION_MILESTONES) {
      // Fires as the countdown crosses the mark, not before it reaches it.
      if (minutesRemaining <= milestone && minutesRemaining > milestone - 1) {
        raise({
          id: `session:${milestone}`,
          kind: 'session',
          severity: milestone <= 5 ? 'urgent' : 'info',
          message: `${milestone} minute${milestone === 1 ? '' : 's'} remaining`,
        });
      }
    }
  }

  return found;
}
